import React from 'react';
import axios from 'axios';
import _ from 'lodash';
import queryString from 'query-string';
import { AuthService } from './AuthService/AuthService';
import { msalInstance } from '..';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { getConfig } from '../config';
import { clearUserData } from 'utils/cleardata';

const constructQueryString = (query) => {
  const queryParams = [];

  _.forIn(query, (value, key) => {
    if (_.isObject(value) || _.isArray(value)) {
      console.warn('Encountered non-primitive value in query values. Skipping.');
      return;
    }

    queryParams.push(`${key}=${value}`);
  });

  if (_.isEmpty(queryParams)) {
    return '';
  }

  const combinedParams = _.join(queryParams, '&');
  return `?${combinedParams}`;
};

export default class BaseAPI extends React.Component {
  static contextType = AuthService;
  constructor({ baseURL, tsiToken }) {
    super();
    this.auth = null;
    this.requester = axios.create({ baseURL });
    if (tsiToken !== undefined) {
      this.requester.interceptors.request.use(
        async (config) => {
          return {
            ...config,
            headers: {
              ...config.headers,
              authorization: `Bearer ${tsiToken}`
            }
          };
        },
        (error) => Promise.reject(error)
      );
      return;
    }
    this.requester.interceptors.request.use(
      async (config) => {
        const accounts = msalInstance.getAllAccounts();
        if (accounts?.[0] == null) {
          clearUserData();
          return msalInstance.logoutRedirect({
            postLogoutRedirectUri: '/'
          });
        }
        const account = msalInstance.getAccountByLocalId(accounts[0].localAccountId ?? null);
        const request = {
          scopes: [getConfig().msalConfig.auth.clientId],
          account: account
        };
        return msalInstance.acquireTokenSilent(request).then((response) => {
          config.headers = {
            ...config.headers,
            authorization: `Bearer ${response.idToken}`
          };
          return config;
        }).catch((e) => {
          clearUserData();
          if (e instanceof InteractionRequiredAuthError) {
            msalInstance.acquireTokenRedirect(request);
          }
        });

      },
      (error) => Promise.reject(error)
    );

  }

  createRequest = async (method, path, body, signal) => {
    const req = {
      method: method,
      url: path,
      data: body,
      signal
    };
    const currentOrgId = sessionStorage.getItem('currentOrgId');
    const updated = { ...req,
      ...(currentOrgId ? { headers: { 'x-bt-org-id': currentOrgId } } : ''),
      ...(
        this.auth ?
          { headers: {
            Authorization: `Bearer ${await this.auth.getToken()}`
          } } :
          null
      )
    };
    return updated;
  };

  request = (method, path, body, cancelSignal) => {
    return this.createRequest(method, path, body, cancelSignal)
      .then((request) => {
        return this.requester.request(request);
      })
      .then(result => result.data);
  };

  requestGet = (path, query, cancelSignal) => {
    if (query) {
      const q = queryString.stringifyUrl({ url: path, query: query }, { arrayFormat: 'comma' });
      return this.request('get', q, null, cancelSignal);
    }
    return this.request('get', path, null, cancelSignal);
  };

  requestDelete = (path, query, cancelSignal) => {
    const _queryString = query ? constructQueryString(query) : '';

    return this.request('delete', `${path}${_queryString}`, null, cancelSignal);
  };

  requestPost = async (path, body, cancelSignal) => {
    return this.request('post', path, body, cancelSignal);
  };

  requestPut = async (path, body, cancelSignal) => {
    return this.request('put', path, body, cancelSignal);
  };

  requestPatch = async (path, body, cancelSignal) => {
    return this.request('patch', path, body, cancelSignal);
  };
}
// BaseAPI.contextType = AuthService;
